import React, { useState, useCallback } from 'react';
import { List } from '@refinedev/antd';
import { useInvalidate } from '@refinedev/core';
import {
  Table,
  Space,
  Tag,
  Input,
  Select,
  Button,
  message,
  Typography,
  Popconfirm,
  Tooltip,
} from 'antd';
import {
  SearchOutlined,
  ReloadOutlined,
  StopOutlined,
  CheckOutlined,
  ClearOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { Link } from 'react-router-dom';
import { axiosInstance, API_BASE_URL } from '../../providers/dataProvider';
import { ALL_DISH_CATEGORIES, CatalogSource } from '@wellverse/shared-ts';

const { Text } = Typography;

type IssueFilter = 'any' | 'all_zero' | 'suspect' | 'portion_unknown';
type ActiveFilter = 'active' | 'inactive' | 'all';

const SOURCE_OPTIONS = [
  { label: 'All sources', value: '' },
  { label: 'Wellcome', value: CatalogSource.WELLCOME },
  { label: 'AI scan (AI_GENERATED)', value: CatalogSource.AI_GENERATED },
  { label: 'Manual / Excel import (MANUAL)', value: CatalogSource.MANUAL },
  { label: 'USDA catalog', value: CatalogSource.USDA },
  { label: 'CFS catalog', value: CatalogSource.CFS },
];

const SOURCE_TAG_COLOR: Record<string, string> = {
  WELLCOME: 'gold',
  AI_GENERATED: 'magenta',
  MANUAL: 'blue',
  USDA: 'green',
  CFS: 'cyan',
};

interface QualityRow {
  id: string;
  name: string;
  category: string;
  type: string;
  standardPortionGrams: number;
  calories: number;
  protein: number;
  fat: number;
  carbs: number;
  fiber: number;
  sodium: number;
  source: string;
  isActive: boolean;
  verifiedBy: string | null;
  externalId: string | null;
  nutritionQuality: string | null;
  portionUnknown: boolean;
  atwaterRatio: number | null;
  packageSize: string | null;
  createdAt: string;
  issues: {
    allZero: boolean;
    suspect: boolean;
    portionUnknown: boolean;
  };
}

interface QualityListResponse {
  items: QualityRow[];
  total: number;
  page: number;
  limit: number;
  counts: {
    allZero: number;
    suspect: number;
    portionUnknown: number;
    total: number;
  };
}

export const QualityCleanupList: React.FC = () => {
  const invalidate = useInvalidate();

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<string | undefined>(undefined);
  const [issue, setIssue] = useState<IssueFilter>('any');
  const [source, setSource] = useState<string>('');
  const [activeFilter, setActiveFilter] = useState<ActiveFilter>('active');

  const [data, setData] = useState<QualityListResponse>({
    items: [],
    total: 0,
    page: 1,
    limit: 20,
    counts: { allZero: 0, suspect: 0, portionUnknown: 0, total: 0 },
  });
  const [loading, setLoading] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [actionLoading, setActionLoading] = useState(false);
  /** Shown after header "select all" — offers delete across all matching filters. */
  const [showDeleteAllMatching, setShowDeleteAllMatching] = useState(false);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('limit', String(limit));
      params.set('issue', issue);
      if (source) params.set('source', source);
      if (activeFilter === 'active') params.set('isActive', 'true');
      if (activeFilter === 'inactive') params.set('isActive', 'false');
      if (search) params.set('search', search);
      if (category) params.set('category', category);

      const res = await axiosInstance.get(
        `${API_BASE_URL}/dish-catalog/admin/quality-issues?${params}`,
      );
      const payload = res.data?.data ?? res.data;
      setData(
        payload ?? {
          items: [],
          total: 0,
          page,
          limit,
          counts: { allZero: 0, suspect: 0, portionUnknown: 0, total: 0 },
        },
      );
    } catch (err) {
      message.error('Failed to load quality issues');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page, limit, search, category, issue, source, activeFilter]);

  React.useEffect(() => {
    fetchList();
  }, [fetchList]);

  const refreshAfterAction = () => {
    setSelectedRowKeys([]);
    setShowDeleteAllMatching(false);
    fetchList();
    invalidate({ resource: 'dish-catalog', invalidates: ['list'] });
    invalidate({ resource: 'ai-review', invalidates: ['list'] });
  };

  const selectedIds = selectedRowKeys.map(String);

  const doDeactivate = async (ids: string[]) => {
    if (ids.length === 0) return;
    setActionLoading(true);
    try {
      const res = await axiosInstance.post(
        `${API_BASE_URL}/dish-catalog/admin/bulk-deactivate`,
        { ids },
      );
      const payload = res.data?.data ?? res.data;
      message.success(
        `Deactivated ${payload?.updated ?? 0} · skipped ${payload?.skipped ?? 0}`,
      );
      refreshAfterAction();
    } catch (err: any) {
      message.error(err?.response?.data?.message ?? 'Deactivate failed');
    } finally {
      setActionLoading(false);
    }
  };

  const doMarkReviewed = async (ids: string[]) => {
    if (ids.length === 0) return;
    setActionLoading(true);
    try {
      const res = await axiosInstance.post(
        `${API_BASE_URL}/dish-catalog/admin/bulk-review`,
        { ids },
      );
      const payload = res.data?.data ?? res.data;
      message.success(
        `Marked reviewed ${payload?.updated ?? 0} · skipped ${payload?.skipped ?? 0}`,
      );
      refreshAfterAction();
    } catch (err: any) {
      message.error(err?.response?.data?.message ?? 'Mark reviewed failed');
    } finally {
      setActionLoading(false);
    }
  };

  const doDeactivateAndReview = async (ids: string[]) => {
    if (ids.length === 0) return;
    setActionLoading(true);
    try {
      const [deactRes, reviewRes] = await Promise.all([
        axiosInstance.post(`${API_BASE_URL}/dish-catalog/admin/bulk-deactivate`, { ids }),
        axiosInstance.post(`${API_BASE_URL}/dish-catalog/admin/bulk-review`, { ids }),
      ]);
      const d = deactRes.data?.data ?? deactRes.data;
      const r = reviewRes.data?.data ?? reviewRes.data;
      message.success(
        `Deactivated ${d?.updated ?? 0}, reviewed ${r?.updated ?? 0}`,
      );
      refreshAfterAction();
    } catch (err: any) {
      message.error(err?.response?.data?.message ?? 'Cleanup failed');
    } finally {
      setActionLoading(false);
    }
  };

  const doDelete = async (ids: string[]) => {
    if (ids.length === 0) return;
    setActionLoading(true);
    try {
      const res = await axiosInstance.post(
        `${API_BASE_URL}/dish-catalog/admin/bulk-delete`,
        { ids },
      );
      const payload = res.data?.data ?? res.data;
      message.success(
        `Deleted ${payload?.deleted ?? 0} · skipped ${payload?.skipped ?? 0}`,
      );
      refreshAfterAction();
    } catch (err: any) {
      message.error(err?.response?.data?.message ?? 'Delete failed');
    } finally {
      setActionLoading(false);
    }
  };

  const buildFilterBody = () => {
    const body: Record<string, unknown> = { issue };
    if (source) body.source = source;
    if (activeFilter === 'active') body.isActive = true;
    if (activeFilter === 'inactive') body.isActive = false;
    if (search) body.search = search;
    if (category) body.category = category;
    return body;
  };

  const doDeleteAllMatching = async () => {
    if (data.total === 0) return;
    setActionLoading(true);
    try {
      const isBroad =
        issue === 'any' && !source && !search && !category;
      const res = await axiosInstance.post(
        `${API_BASE_URL}/dish-catalog/admin/bulk-delete-matching`,
        {
          ...buildFilterBody(),
          confirmTotal: data.total,
          ...(isBroad ? { forceBroadDelete: true } : {}),
        },
      );
      const payload = res.data?.data ?? res.data;
      message.success(
        `Deleted ${payload?.deleted ?? 0} of ${payload?.matched ?? data.total} matching`,
      );
      refreshAfterAction();
    } catch (err: any) {
      message.error(err?.response?.data?.message ?? 'Delete all matching failed');
    } finally {
      setActionLoading(false);
    }
  };

  const { counts } = data;

  return (
    <List resource="quality-cleanup" title="Quality Cleanup">
      <div
        style={{
          marginBottom: 16,
          display: 'flex',
          gap: 12,
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        <Input.Search
          prefix={<SearchOutlined />}
          placeholder="Search name, alias, or external ID..."
          allowClear
          style={{ width: 280 }}
          onSearch={(v) => {
            setPage(1);
            setSearch(v);
          }}
        />
        <Select
          placeholder="Category"
          allowClear
          showSearch
          style={{ width: 180 }}
          value={category}
          onChange={(v) => {
            setPage(1);
            setCategory(v);
          }}
          options={ALL_DISH_CATEGORIES.map((c) => ({ label: c, value: c }))}
        />
        <Select
          style={{ width: 240 }}
          value={source}
          onChange={(v) => {
            setPage(1);
            setSource(v);
          }}
          options={SOURCE_OPTIONS}
        />
        <Select
          style={{ width: 180 }}
          value={issue}
          onChange={(v) => {
            setPage(1);
            setIssue(v);
          }}
          options={[
            { label: 'Any issue', value: 'any' },
            { label: 'All-zero macros', value: 'all_zero' },
            { label: 'Suspect (Atwater)', value: 'suspect' },
            { label: 'Portion unknown', value: 'portion_unknown' },
          ]}
        />
        <Select
          style={{ width: 140 }}
          value={activeFilter}
          onChange={(v) => {
            setPage(1);
            setActiveFilter(v);
          }}
          options={[
            { label: 'Active', value: 'active' },
            { label: 'Inactive', value: 'inactive' },
            { label: 'All', value: 'all' },
          ]}
        />
        <Button icon={<ReloadOutlined />} onClick={fetchList}>
          Refresh
        </Button>
      </div>

      <Space wrap style={{ marginBottom: 12 }}>
        <Tag
          color={issue === 'any' ? 'blue' : undefined}
          style={{ cursor: 'pointer' }}
          onClick={() => {
            setPage(1);
            setIssue('any');
          }}
        >
          Total issues: {counts.total}
        </Tag>
        <Tag
          color={issue === 'all_zero' ? 'red' : undefined}
          style={{ cursor: 'pointer' }}
          onClick={() => {
            setPage(1);
            setIssue('all_zero');
          }}
        >
          All-zero: {counts.allZero}
        </Tag>
        <Tag
          color={issue === 'suspect' ? 'orange' : undefined}
          style={{ cursor: 'pointer' }}
          onClick={() => {
            setPage(1);
            setIssue('suspect');
          }}
        >
          Suspect: {counts.suspect}
        </Tag>
        <Tag
          color={issue === 'portion_unknown' ? 'purple' : undefined}
          style={{ cursor: 'pointer' }}
          onClick={() => {
            setPage(1);
            setIssue('portion_unknown');
          }}
        >
          Portion unknown: {counts.portionUnknown}
        </Tag>
      </Space>

      <div style={{ marginBottom: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <Popconfirm
          title={`Deactivate ${selectedIds.length} selected row(s)?`}
          disabled={selectedIds.length === 0}
          onConfirm={() => doDeactivate(selectedIds)}
        >
          <Button
            icon={<StopOutlined />}
            disabled={selectedIds.length === 0}
            loading={actionLoading}
          >
            Deactivate ({selectedIds.length})
          </Button>
        </Popconfirm>
        <Popconfirm
          title={`Mark ${selectedIds.length} selected as reviewed?`}
          disabled={selectedIds.length === 0}
          onConfirm={() => doMarkReviewed(selectedIds)}
        >
          <Button
            icon={<CheckOutlined />}
            disabled={selectedIds.length === 0}
            loading={actionLoading}
          >
            Mark reviewed ({selectedIds.length})
          </Button>
        </Popconfirm>
        <Tooltip title="Deactivate and mark reviewed — leaves AI Scan Logs and production search">
          <Popconfirm
            title={`Deactivate + mark reviewed for ${selectedIds.length} row(s)?`}
            disabled={selectedIds.length === 0}
            onConfirm={() => doDeactivateAndReview(selectedIds)}
          >
            <Button
              type="primary"
              danger
              icon={<ClearOutlined />}
              disabled={selectedIds.length === 0}
              loading={actionLoading}
            >
              Deactivate + review ({selectedIds.length})
            </Button>
          </Popconfirm>
        </Tooltip>
        <Popconfirm
          title={`Permanently delete ${selectedIds.length} selected row(s)?`}
          description="This cannot be undone. Prefer Deactivate if unsure."
          disabled={selectedIds.length === 0}
          okText="Delete selected"
          okButtonProps={{ danger: true }}
          onConfirm={() => doDelete(selectedIds)}
        >
          <Button
            danger
            icon={<DeleteOutlined />}
            disabled={selectedIds.length === 0}
            loading={actionLoading}
          >
            Delete selected ({selectedIds.length})
          </Button>
        </Popconfirm>
      </div>

      {showDeleteAllMatching && data.total > 0 && (
        <div
          style={{
            marginBottom: 12,
            padding: '10px 12px',
            background: '#fff2f0',
            border: '1px solid #ffccc7',
            borderRadius: 6,
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            flexWrap: 'wrap',
          }}
        >
          <Text>
            All <b>{selectedIds.length}</b> on this page selected
            {data.total > selectedIds.length
              ? ` — ${data.total} rows match current filters.`
              : '.'}
          </Text>
          <Popconfirm
            title={`Permanently delete ALL ${data.total} matching row(s)?`}
            description={
              issue === 'any' && !source && !search && !category
                ? 'BROAD DELETE: issue=any with no source/search/category. Max 2000 rows. Cannot be undone.'
                : 'Uses current filters (issue / source / active / search / category). Max 2000 rows. Cannot be undone.'
            }
            okText="Delete all matching"
            okButtonProps={{ danger: true }}
            onConfirm={() => doDeleteAllMatching()}
          >
            <Button
              danger
              type="primary"
              icon={<DeleteOutlined />}
              loading={actionLoading}
            >
              Delete all matching ({data.total})
            </Button>
          </Popconfirm>
          <Button
            type="link"
            onClick={() => {
              setSelectedRowKeys([]);
              setShowDeleteAllMatching(false);
            }}
          >
            Clear selection
          </Button>
        </div>
      )}

      <Table
        rowKey="id"
        dataSource={data.items}
        loading={loading}
        scroll={{ x: 1600 }}
        size="middle"
        rowSelection={{
          selectedRowKeys,
          onChange: (keys) => {
            setSelectedRowKeys(keys);
            const pageIds = data.items.map((r) => r.id);
            const allOnPageSelected =
              pageIds.length > 0 && pageIds.every((id) => keys.includes(id));
            setShowDeleteAllMatching(allOnPageSelected);
          },
          onSelectAll: (selected) => {
            setShowDeleteAllMatching(selected && data.total > 0);
          },
        }}
        pagination={{
          current: data.page,
          pageSize: data.limit,
          total: data.total,
          onChange: (p, ps) => {
            setPage(p);
            setLimit(ps);
            setSelectedRowKeys([]);
            setShowDeleteAllMatching(false);
          },
          showSizeChanger: true,
          pageSizeOptions: [20, 50, 100, 500],
        }}
      >
        <Table.Column
          dataIndex="name"
          title="Name"
          fixed="left"
          width={220}
          render={(name: string, record: QualityRow) => (
            <Link to={`/dish-catalog/show/${record.id}`}>{name}</Link>
          )}
        />
        <Table.Column
          dataIndex="source"
          title="Source"
          width={130}
          render={(v: string) => (
            <Tag color={SOURCE_TAG_COLOR[v] ?? 'default'}>{v}</Tag>
          )}
        />
        <Table.Column
          dataIndex="externalId"
          title="External ID"
          width={160}
          render={(v: string | null) => v || <Text type="secondary">—</Text>}
        />
        <Table.Column dataIndex="category" title="Category" width={120} />
        <Table.Column
          title="Issues"
          width={200}
          render={(_: unknown, record: QualityRow) => (
            <Space wrap size={4}>
              {record.issues.allZero && <Tag color="red">Zero</Tag>}
              {record.issues.suspect && (
                <Tag color="orange">
                  Suspect
                  {record.atwaterRatio != null
                    ? ` ${record.atwaterRatio.toFixed(2)}`
                    : ''}
                </Tag>
              )}
              {record.issues.portionUnknown && (
                <Tag color="purple">Portion?</Tag>
              )}
            </Space>
          )}
        />
        <Table.Column
          dataIndex="standardPortionGrams"
          title="Portion"
          width={90}
          render={(v: number, r: QualityRow) =>
            r.packageSize ? `${v} (${r.packageSize})` : v
          }
        />
        <Table.Column dataIndex="calories" title="kcal" width={70} />
        <Table.Column dataIndex="protein" title="P" width={60} />
        <Table.Column dataIndex="fat" title="F" width={60} />
        <Table.Column dataIndex="carbs" title="C" width={60} />
        <Table.Column
          dataIndex="isActive"
          title="Active"
          width={80}
          render={(v: boolean) =>
            v ? <Tag color="green">Yes</Tag> : <Tag>No</Tag>
          }
        />
        <Table.Column
          dataIndex="verifiedBy"
          title="Reviewed"
          width={90}
          render={(v: string | null) =>
            v ? <Tag color="blue">Yes</Tag> : <Tag>No</Tag>
          }
        />
        <Table.Column
          dataIndex="createdAt"
          title="Created"
          width={140}
          render={(ts: string) => dayjs(ts).format('YYYY-MM-DD HH:mm')}
        />
      </Table>
    </List>
  );
};
